import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  TrendingUp,
  Activity,
  ArrowRightLeft,
  Star,
  Filter,
  CandlestickChart,
  CreditCard,
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  Shield,
  Play,
  Building2,
  Home,
  Lock,
} from "lucide-react";
import { useProfile } from "../../contexts/ProfileContext";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { hasBootcampAccess, BOOTCAMP_ONLY_ROUTES } from "../../utils/subscriptionAccess";
const getSupabaseUrl = () => {
  return (import.meta as any).env?.VITE_SUPABASE_URL || 'https://bandarmolony.supabase.co';
};
const logoUrl = `${getSupabaseUrl()}/storage/v1/object/public/assets/images/logo_bandarmolony_v2.png`;

/** ===== MENU ===== */
interface MenuItem {
  title: string;
  icon: React.ElementType;
  url: string;
  route: string;
  roles?: string[];
  iconClassName?: string;
  children?: {
    title: string;
    url: string;
    route: string;
    roles?: string[];
    badge?: string;
  }[];
}

const menuItems: MenuItem[] = [
  { title: "Home", icon: Home, url: "#", route: "home" },
  { title: "Watchlist", icon: Eye, url: "#", route: "dashboard" },
  { title: "Bid-Offer Replay", icon: Star, url: "#", route: "stock-transaction/orderbook-replay", iconClassName: "text-yellow-400 fill-yellow-400" },
  {
    title: "Screener 360°",
    icon: Filter,
    url: "#",
    route: "story",
    children: [
      { title: "Accumulation Distribution", url: "#", route: "story/accumulation-distribution" },
      { title: "AI Monitoring", url: "#", route: "story/ai-monitoring", roles: ["admin", "developer"] },
      { title: "AI Chatbot", url: "#", route: "story/ai-chatbot", roles: ["admin", "developer"] },
      { title: "Volume", url: "#", route: "story/volume" },
      { title: "SID", url: "#", route: "story/sid" },
      { title: "Insider", url: "#", route: "market-analysis/insider" },
      { title: "Free Float", url: "#", route: "market-analysis/free-float" },
      { title: "High Concentration List", url: "#", route: "market-analysis/hcl" },
    ],
  },
  {
    title: "Broker Analysis",
    icon: Activity,
    url: "#",
    route: "broker-activity",
    children: [
      { title: "Broker Summary 5D", url: "#", route: "broker-activity/new-summary" },
      { title: "Broker Summary 5D (Dev)", url: "#", route: "broker-activity/new-summary-dev", roles: ["admin", "developer"] },
      { title: "Broker Transaction", url: "#", route: "broker-activity/new-transaction" },
      { title: "Broker Inventory", url: "#", route: "broker-activity/inventory" },
    ],
  },
  {
    title: "Stock Analysis",
    icon: ArrowRightLeft,
    url: "#",
    route: "stock-transaction",
    children: [
      { title: "Trade Footprints", url: "#", route: "stock-transaction/done-summary" },
      { title: "Trade Detail", url: "#", route: "stock-transaction/done-detail" },
      { title: "Bid-Offer Replay", url: "#", route: "stock-transaction/orderbook-replay", badge: "star" },
    ],
  },
  {
    title: "Market Rotation",
    icon: TrendingUp,
    url: "#",
    route: "market-rotation",
    roles: ["developer"],
    children: [
      { title: "Relative Rotation Graph", url: "#", route: "market-rotation/rrg" },
      { title: "Relative Rotation Curve", url: "#", route: "market-rotation/rrc" },
      { title: "Seasonality", url: "#", route: "market-rotation/seasonality" },
      { title: "Trend Filter", url: "#", route: "market-rotation/trend-filter" },
    ],
  },
  {
    title: "Astrology",
    icon: Star,
    url: "#",
    route: "astrology",
    roles: ["developer", "admin"],
    children: [
      { title: "Input Data", url: "#", route: "astrology/lunar" },
      { title: "Bazimology", url: "#", route: "astrology/bazi-cycle" },
    ],
  },
  {
    title: "Technical Analysis",
    icon: CandlestickChart,
    url: "#",
    route: "technical-analysis",
    roles: ["developer", "admin"],
  },
];

/** ===== BOTTOM MENU ===== */
const bottomMenuItems = [
  { title: "Subscription", icon: CreditCard, url: "#", route: "subscription" },
];

/** ===== ADMIN MENU ===== */
const adminMenuItems = [
  { title: "Admin Dashboard", icon: Shield, url: "#", route: "admin" },
];

/** ===== DEVELOPER MENU ===== */
const developerMenuItems = [
  { title: "Developer Dashboard", icon: Shield, url: "#", route: "developer" },
];

interface SidebarProps {
  isOpen: boolean;                 // pinned
  onToggle: () => void;            // toggle pinned
  currentRoute: string;
}

export function Sidebar({
  isOpen,
  onToggle,
  currentRoute,
}: SidebarProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { profile } = useProfile();
  const { signOut } = useAuth();
  const { showToast } = useToast();
  const isExpanded = isOpen || isHovered;

  const toggleExpanded = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title) ? prev.filter((i) => i !== title) : [...prev, title],
    );
  };


  const handleNavigation = async (route: string) => {
    if (route === 'logout') {
      try {
        // Trigger AuthContext signOut asynchronously
        signOut();

        // Show toast immediately
        showToast({
          type: 'success',
          title: 'Logout Berhasil!',
          message: 'Anda telah berhasil logout.',
        });

        // Navigate immediately via React Router for smooth transition
        navigate('/auth', { replace: true });

        // Close sidebar only on mobile
        if (window.innerWidth < 1024) onToggle();

      } catch (error) {
        console.error('Logout error:', error);
        // Show error toast if logout fails
        showToast({
          type: 'error',
          title: 'Logout Gagal',
          message: 'Terjadi kesalahan saat logout.',
        });
      }
    } else if (route === '/profile') {
      // Navigate directly to profile page
      navigate('/profile');
      // Close sidebar only on mobile
      if (window.innerWidth < 1024) onToggle();
    } else if (route === 'dashboard') {
      // Navigate to dashboard
      navigate('/dashboard');
      // Close sidebar only on mobile
      if (window.innerWidth < 1024) onToggle();
    } else {
      // Navigate to specific routes without /dashboard prefix
      navigate(`/${route}`);
      // Close sidebar only on mobile
      if (window.innerWidth < 1024) onToggle();
    }
  };

  return (
    <>
      {/* Overlay hanya untuk mobile ketika sidebar terbuka */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[10000] lg:hidden bg-black/30 backdrop-blur-[2px]"
          onClick={onToggle}
        />
      )}

      <div
        className={`
          fixed inset-y-0 left-0 z-[10001] bg-card border-r border-border
          transform transition-all duration-300 ease-in-out h-screen
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${isExpanded ? "w-64" : "w-16"}
          overflow-hidden overflow-x-hidden
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative flex flex-col h-full">
          {/* Header */}
          <div className={`flex items-center ${isExpanded ? "justify-start" : "justify-center"} p-3 border-b border-border h-14`}>
            {/* Logo kiri – left aligned */}
            <div className="flex items-center gap-0.5 min-w-0">
              {isExpanded && (
                <span className="text-card-foreground font-semibold whitespace-nowrap truncate">Bandarmolo</span>
              )}
              <div className="h-8 w-8 flex items-center justify-center flex-shrink-0">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-8 w-8 select-none object-contain flex-shrink-0"
                  draggable={false}
                />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 py-2 flex flex-col min-h-0 overflow-hidden overflow-x-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              {/* Admin Menu Items - Only show for admin users - AT THE TOP */}
              {profile?.role === 'admin' && (
                <>
                  {isExpanded && (
                    <div className="px-4 mb-2">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Administration
                      </h3>
                    </div>
                  )}
                  <nav className="space-y-0.5 px-2 mb-4">
                    {adminMenuItems.map((item) => (
                      <div
                        key={item.title}
                        onClick={() => handleNavigation(item.route)}
                        className={`
                          flex items-center gap-3 px-3 py-3 text-sm rounded-md transition-all duration-200 cursor-pointer min-h-12 group min-w-0
                          ${!isExpanded ? "justify-center" : ""}
                          ${currentRoute === item.route
                            ? "bg-accent text-accent-foreground shadow-sm"
                            : "text-card-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:bg-accent/80"
                          }
                        `}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {isExpanded && <span className="whitespace-nowrap text-sm truncate min-w-0">{item.title}</span>}
                      </div>
                    ))}
                  </nav>
                </>
              )}

              {/* Developer Menu Items - Only show for developer users - AT THE TOP */}
              {profile?.role === 'developer' && (
                <>
                  {isExpanded && (
                    <div className="px-4 mb-2">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Development
                      </h3>
                    </div>
                  )}
                  <nav className="space-y-0.5 px-2 mb-4">
                    {developerMenuItems.map((item) => (
                      <div
                        key={item.title}
                        onClick={() => handleNavigation(item.route)}
                        className={`
                          flex items-center gap-3 px-3 py-3 text-sm rounded-md transition-all duration-200 cursor-pointer min-h-12 group min-w-0
                          ${!isExpanded ? "justify-center" : ""}
                          ${currentRoute === item.route
                            ? "bg-accent text-accent-foreground shadow-sm"
                            : "text-card-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:bg-accent/80"
                          }
                        `}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {isExpanded && <span className="whitespace-nowrap text-sm truncate min-w-0">{item.title}</span>}
                      </div>
                    ))}
                  </nav>
                </>
              )}



              <nav className="space-y-0.5 px-2">
                {menuItems.map((item) => {
                  if (item.roles && !item.roles.includes(profile?.role || '')) return null;

                  const active =
                    currentRoute === item.route ||
                    currentRoute.startsWith(item.route + "/");

                  const visibleChildren = item.children?.filter(child => {
                    if (child.roles && !child.roles.includes(profile?.role || '')) return false;
                    return true;
                  });

                  return (
                    <div key={item.title}>
                      <div
                        className={`
                          flex items-center justify-between px-3 py-3 text-sm rounded-md transition-all duration-200 cursor-pointer min-h-12 group min-w-0
                          ${!isExpanded ? "justify-center" : ""}
                          ${active
                            ? "bg-accent text-accent-foreground shadow-sm"
                            : "text-card-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:bg-accent/80"
                          }
                        `}
                        onClick={() => {
                          if (item.children && isExpanded) {
                            toggleExpanded(item.title);
                          } else {
                            handleNavigation(item.route);
                          }
                        }}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <item.icon className={`w-5 h-5 flex-shrink-0 ${item.iconClassName || ''}`} />
                          {isExpanded && (
                            <span className="whitespace-nowrap text-sm truncate">{item.title}</span>
                          )}
                        </div>
                        {item.children && isExpanded && (
                          <div className="flex-shrink-0">
                            {expandedItems.includes(item.title) ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </div>
                        )}
                      </div>

                      {item.children &&
                        isExpanded &&
                        expandedItems.includes(item.title) && (
                          <div className="ml-6 mt-0.5 space-y-0.5">
                            {visibleChildren?.map((child) => {
                              const bootcampLocked =
                                BOOTCAMP_ONLY_ROUTES.includes(child.route) && !hasBootcampAccess(profile);
                              return (
                                <div
                                  key={child.title}
                                  onClick={() => {
                                    if (bootcampLocked) {
                                      showToast({
                                        type: 'warning',
                                        title: 'Menu khusus bootcamp',
                                        message: 'Menu ini hanya tersedia untuk user yang sudah mengikuti bootcamp.',
                                      });
                                      return;
                                    }
                                    handleNavigation(child.route);
                                  }}
                                  title={bootcampLocked ? 'Menu khusus bootcamp' : undefined}
                                  className={`
                                    flex items-center gap-1.5 px-2 py-1 text-xs rounded-md transition-all duration-200 cursor-pointer group
                                    ${currentRoute === child.route
                                      ? "bg-accent text-accent-foreground shadow-sm font-medium"
                                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:bg-accent/80"
                                    }
                                  `}
                                >
                                  <span>{child.title}</span>
                                  {bootcampLocked && (
                                    <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  )}
                                  {child.badge === 'star' && (
                                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                                  )}
                                  {child.badge === 'Beta' && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-black bg-[#00ff00] rounded-sm leading-none flex-shrink-0 uppercase ml-auto">
                                      BETA
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  );
                })}
              </nav>
            </div>

            {/* Bottom */}
            <div className="border-t border-border pt-2 px-2 space-y-0.5">
              {/* Video Tutorial Menu Item */}
              <div
                onClick={() => handleNavigation('video-tutorial')}
                className={`
                  flex items-center gap-3 px-3 py-3 text-sm rounded-md transition-all duration-200 cursor-pointer min-h-12 group min-w-0
                  ${!isExpanded ? "justify-center" : ""}
                  ${currentRoute === 'video-tutorial'
                    ? "bg-accent text-accent-foreground shadow-sm"
                    : "text-card-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:bg-accent/80"
                  }
                `}
              >
                <Play className="w-5 h-5 flex-shrink-0" />
                {isExpanded && <span className="whitespace-nowrap text-sm truncate min-w-0">Video Tutorial</span>}
              </div>


              {bottomMenuItems.map((item) => (
                <div
                  key={item.title}
                  onClick={() => handleNavigation(item.route)}
                  className={`
                    flex items-center gap-3 px-3 py-3 text-sm rounded-md transition-all duration-200 cursor-pointer min-h-12 group min-w-0
                    ${!isExpanded ? "justify-center" : ""}
                    ${currentRoute === item.route
                      ? "bg-accent text-accent-foreground shadow-sm"
                      : "text-card-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm active:bg-accent/80"
                    }
                  `}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {isExpanded && <span className="whitespace-nowrap text-sm truncate min-w-0">{item.title}</span>}
                </div>
              ))}

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
